import { Injectable, inject } from "@angular/core";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { MetadataService } from "./metadata.service";
/**
 * These enum members match the const string values defined in Tyler.Api.Metadata.InputTypeAttribute
 * @see <RepositoryRoot>\Shared\Tyler.API\MetaData\Attributes.cs
 */
export enum InputType
{
    Text,
    password,
    Label,
    Header,
    Select,
    MultiSelect,
    DualList,
    TextArea,
    RichText,
    Date,
    Time,
    Time24,
    Check,
    Number,
    NumberWithoutDisplayFiltering,
    Currency,
    Percent,
    TypeAhead,
    FilePicker,
    Button,
    Photo,
    Color,
    DateTime,
    Calendar,
    MonthCalendar,
    Chart,
    Script,
    Html,
    LabelValue,
    PhoneNumber,
    PhoneNumberWithExtension,
    Link,
    Badge,
    InlineMessage,
    Tree,
    SpatialMap
}

@Injectable()
export class MetadataInputTypeHelperService
{
    private readonly $metadataService = inject(MetadataService);

    asInputType(inputType: string): InputType
    {
        return InputType[inputType];
    }

    includeContainer(inputType: string): boolean
    {
        return (
            this.isText(inputType) ||
            this.isTextArea(inputType) ||
            this.isRichText(inputType) ||
            this.isTypeAhead(inputType) ||
            this.isSelect(inputType) ||
            this.isMultiSelect(inputType) ||
            this.isDualList(inputType) ||
            this.isDate(inputType) ||
            this.isCheck(inputType) ||
            this.isNumeric(inputType) ||
            this.isCurrency(inputType) ||
            this.isPercent(inputType) ||
            this.isFileInput(inputType) ||
            this.isColor(inputType) ||
            this.isTime(inputType) ||
            this.isTime24(inputType) ||
            this.isDateTime(inputType) ||
            this.isPhoneNumber(inputType) ||
            this.isPhoneNumberWithExtension(inputType) ||
            this.isPhoto(inputType)
        );
    }

    isTextBased(inputType: string): boolean
    {
        switch (this.asInputType(inputType))
        {
            case InputType.Text:
            case InputType.TextArea:
            case InputType.RichText:
            case InputType.Select:
            case InputType.password:
            case InputType.MultiSelect:
                return true;
            default:
                return false;
        }
    }

    isText(inputType: string): boolean
    {
        return (
            this.asInputType(inputType) === InputType.Text ||
            this.asInputType(inputType) === InputType.password
        );
    }

    isTextArea(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.TextArea;
    }

    isRichText(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.RichText;
    }

    isSelect(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Select;
    }

    isMultiSelect(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.MultiSelect;
    }

    isDualList(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.DualList;
    }

    isLookup(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.MultiSelect ||
            this.asInputType(inputType) === InputType.Select ||
            this.asInputType(inputType) === InputType.TypeAhead;
    }

    isFileInput(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.FilePicker;
    }

    isDate(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Date;
    }

    isDateTime(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.DateTime;
    }

    isTime(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Time;
    }

    isTime24(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Time24;
    }

    isCheck(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Check;
    }

    isNumeric(inputType: string): boolean
    {
        return (
            this.isNumber(inputType) ||
            this.isNumberWithoutDisplayFiltering(inputType)
        );
    }

    isNumber(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Number;
    }

    isNumberWithoutDisplayFiltering(inputType: string): boolean
    {
        return (
            this.asInputType(inputType) ===
            InputType.NumberWithoutDisplayFiltering
        );
    }

    isCurrency(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Currency;
    }

    isPercent(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Percent;
    }

    isTypeAhead(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.TypeAhead;
    }

    isButton(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Button;
    }

    isPhoto(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Photo;
    }

    isColor(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Color;
    }

    isLabel(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Label;
    }

    isLabelValue(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.LabelValue;
    }

    isHeader(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Header;
    }

    isSpatialMap(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.SpatialMap;
    }

    isCalendar(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Calendar;
    }

    isMonthCalendar(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.MonthCalendar;
    }

    isLink(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Link;
    }

    isChart(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Chart;
    }

    isBadge(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Badge;
    }

    isTree(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Tree;
    }

    isInlineMessage(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.InlineMessage;
    }

    isScript(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Script;
    }

    isHtml(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.Html;
    }

    isPhoneNumber(inputType: string): boolean
    {
        return this.asInputType(inputType) === InputType.PhoneNumber;
    }

    isPhoneNumberWithExtension(inputType: string): boolean
    {
        return (
            this.asInputType(inputType) === InputType.PhoneNumberWithExtension
        );
    }

    delayRecalculateForCellEdit(inputType: string): boolean
    {
        return (
            this.isText(inputType) ||
            this.isTextArea(inputType) ||
            // this.isDate ||
            this.isNumeric(inputType) ||
            this.isCurrency(inputType) ||
            this.isPercent(inputType) ||
            // this.isTime ||
            // this.isDateTime ||
            this.isPhoneNumber(inputType) ||
            this.isPhoneNumberWithExtension(inputType)
        );
    }


    getMinValue(propertyMetadata: PropertyMetadata, model: any, parent: any): number
    {
        if (propertyMetadata.MinValue === 0)
            return 0;
        return propertyMetadata.MinValue
            ? propertyMetadata.MinValue
            : !propertyMetadata.MinValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    propertyMetadata.MinValueProperty,
                    parent,
                    model
                );
    }

    getMaxValue(propertyMetadata: PropertyMetadata, model: any, parent: any): number
    {
        if (propertyMetadata.MaxValue === 0)
            return 0;
        return propertyMetadata.MaxValue
            ? propertyMetadata.MaxValue
            : !propertyMetadata.MaxValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    propertyMetadata.MaxValueProperty,
                    parent,
                    model
                );
    }

    getPrecision(propertyMetadata: PropertyMetadata, model: any, parent: any): number
    {
        return propertyMetadata.Precision
            ? propertyMetadata.Precision
            : !propertyMetadata.PrecisionProperty
                ? 0
                : this.$metadataService.DependentModuleValue(
                    propertyMetadata.PrecisionProperty,
                    parent,
                    model
                ) || 0;
    }

    getScale(propertyMetadata: PropertyMetadata, model: any, parent: any): number
    {
        if (propertyMetadata.Scale === 0)
            return 0;
        return propertyMetadata.Scale
            ? propertyMetadata.Scale
            : !propertyMetadata.ScaleProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    propertyMetadata.ScaleProperty,
                    parent,
                    model
                );
    }

    getMaxLength(propertyMetadata: PropertyMetadata, model: any, parent: any): number
    {
        return propertyMetadata.MaxLength
            ? propertyMetadata.MaxLength
            : !propertyMetadata.MaxLengthProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    propertyMetadata.MaxLengthProperty,
                    parent,
                    model
                );
    }

}
